<template>
  <o-modal
    ref="modal"
    :title="$t('components.waitlist.actions.create.title')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'success', disabled: !valid, callback: handleConfirm },
    ]"
    :loading="loading"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-xl sm:w-full h-full max-h-xl"
    content-class="mx-auto max-w-xl h-full flex flex-col"
  >
    <div class="text-left h-full">
      <o-scroll class="h-full">
        <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">
          {{ $t('general.colon', { label: $t('general.customer') }) }}
        </p>

        <div v-if="customer" class="w-full flex items-center mb-6 bg-gray-100 dark:bg-gray-400/5 rounded-md p-3">
          <o-avatar :items="[customer]" border="" class="flex-shrink-0" />
          <div class="ml-2 text-sm font-normal">
            <p class="leading-none mb-1 text-gray-800 dark:text-gray-100">
              <template v-if="customer.firstName.length || customer.lastName.length">
                {{ customer.firstName }} {{ customer.lastName }}
              </template>
            </p>
            <p class="block text-xs text-gray-700 dark:text-gray-200 leading-none">
              #{{ customer.idCustomer }}
            </p>
          </div>
        </div>

        <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">
          {{ $t('general.colon', { label: $t('general.services') }) }}
        </p>

        <div v-if="services" class="bg-gray-100 dark:bg-gray-400/5 rounded-md p-3 mb-6 flex flex-col divide-y">
          <div v-for="service in services" :key="service.idService" class="w-full flex items-center">
            <o-avatar :items="[service.service]" border="" class="flex-shrink-0" />
            <div class="ml-2 text-sm font-normal">
              <p class="leading-none mb-1 text-gray-800 dark:text-gray-100">
                {{ service.service.title }}
              </p>
              <p class="block text-xs text-gray-700 dark:text-gray-200 leading-none">
                {{ service.employee ? $t('components.waitlist.actions.create.with', { name: service.employee.displayName }) : $t('components.waitlist.actions.create.withAnyone') }}
              </p>
            </div>
          </div>
        </div>

        <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">
          {{ $t('components.waitlist.actions.create.dateTimes') }}
        </p>

        <div class="bg-gray-100 dark:bg-gray-400/5 rounded-md px-3 mb-6 flex flex-col divide-y">
          <div v-for="(item, index) in dates" :key="index" class="w-full flex items-center justify-between py-3">
            <div class="ml-2 text-sm font-normal">
              <p class="leading-none mb-1 text-gray-800 dark:text-gray-100">
                {{ $date.convert(item.date, 'yyyyMMdd', $date.presets.long) }}
              </p>
              <p class="block text-xs text-gray-700 dark:text-gray-200 leading-none">
                {{ $t('components.waitlist.actions.create.timeToTime', { from: item.startTime ? $date.minutes(item.startTime) : $t('waitlist.anyTime'), to: item.finishTime ? $date.minutes(item.finishTime) : $t('components.waitlist.actions.create.anyTime') }) }}
              </p>
            </div>

            <o-button icon="x" variant="danger" size="xs" flat @click="handleRemoveDate(index)" />
          </div>

          <p v-if="!dates.length" class="text-center text-gray-600 dark:text-gray-300 text-sm py-4">
            {{ $t('components.waitlist.actions.create.noDates') }}
          </p>

          <o-form ref="form" class="py-3">
            <div class="flex items-start space-x-2">
              <o-field-group v-slot="{ status }" :label="$t('general.date')" rules="required">
                <date-field v-model="date.date" :status="status" placeholder="DD/MM/YYYY" :augmenter="Augmenters.YMD" />
              </o-field-group>

              <o-field-group :label="$t('components.waitlist.actions.create.fromTime')" :name="$t('components.waitlist.actions.create.fromTime')">
                <time-field v-model="date.startTime" placeholder="HH:MM" :augmenter="Augmenters.minutes" />
              </o-field-group>

              <o-field-group :label="$t('components.waitlist.actions.create.toTime')" :name="$t('components.waitlist.actions.create.toTime')">
                <time-field v-model="date.finishTime" placeholder="HH:MM" :augmenter="Augmenters.minutes" />
              </o-field-group>

              <o-button :disabled="!canAddDate" variant="success" class="mt-6 flex-grow" @click="handleAddDate">
                {{ $t('general.add') }}
              </o-button>
            </div>
          </o-form>
        </div>
      </o-scroll>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import {
  OAvatar,
  OFieldGroup,
  OForm,
} from '@/components/elements'
import {
  SegmentField,
  DateField,
  TimeField,
} from '@/components/fields'
import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'
import { useSegmentStore } from '~/stores/useSegmentStore'

const { $api, $notification, $bus, $i18n } = useNuxtApp()

const segmentStore = useSegmentStore()

const emit = defineEmits<{
  (e: 'success', waitlist: any): void
}>()

const modal = ref<any>()
const form = ref<any>()

const loading = ref(false)
const customer = ref<any>(null)
const services = ref<any[]>([])
const dates = ref<any[]>([])
const date = ref<any>({
  date: null,
  startTime: null,
  finishTime: null,
})

const valid = computed(() => {
  return customer.value && services.value?.length && dates.value?.length
})

const canAddDate = computed(() => Boolean(date.value.date))

async function open ({ customer: _customer, services: _services = [], dates: _dates = [] }: any = {}) {
  await segmentStore.sync()

  customer.value = _customer
  services.value = _services
  dates.value = []

  modal.value.open()
}

async function validate () {
  const formIsValid = await form.value.validate()
  let isValid = Boolean(date.value.date) && formIsValid

  if (isValid && date.value.startTime && date.value.finishTime) {
    isValid = date.value.startTime < date.value.finishTime

    if (!isValid) {
      $notification.error({ title: $i18n.t('components.waitlist.actions.create.error') })
    }
  }

  return isValid
}

async function handleAddDate () {
  const isValid = await validate()

  if (isValid) {
    const d = date.value

    dates.value.push(d)
    date.value = {
      date: null,
      startTime: null,
      finishTime: null,
    }

    form.value.reset()
  }
}

function handleRemoveDate (index: number) {
  dates.value.splice(index, 1)
}

async function handleConfirm () {
  loading.value = true

  if (customer.value.isNewRecord) {
    const result = await saveCustomer()

    if (!result) {
      loading.value = false

      return
    }
  }

  await createWaitlist()

  loading.value = false
}

async function createWaitlist () {
  try {
    const response = await $api.waitlist.create({ customer: customer.value, services: services.value, dates: dates.value })
    const waitlist = response?.data

    if (waitlist) {
      $notification.toast({
        title: $i18n.t('components.waitlist.actions.create.success'),
        variant: 'success',
      })

      emit('success', waitlist)
      $bus.emit.created('Waitlist', waitlist)

      modal.value.close()

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }
}

async function saveCustomer () {
  try {
    const response = await $api.customer.save(customer.value)
    const result = response.data

    if (result) {
      customer.value = result

      $notification.toast({
        title: $i18n.t('customer.createdSuccess'),
        text: $i18n.t('customer.createdSuccessInfo', { name: result.displayName }),
        variant: 'success',
      })

      $bus.emit.created('Customer', result)

      return true
    }
  } catch (e) {
    $notification.toast({
      title: $i18n.t('general.error'),
      text: $i18n.t('customer.createError'),
      variant: 'danger',
    })
  }
}

defineExpose({ open })
</script>
